// Core 
import { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Button,
  Flex, 
  Box,
  Grid,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Text, 
  useColorModeValue
} from '@chakra-ui/react';

// Assets
import { apiService } from 'services/ApiService';
import { ReactComponent as StatePauseIcon } from 'assets/svg/state-pause.svg';
import { ReactComponent as StateDowngradeIcon } from 'assets/svg/state-downgrade.svg';
import { ReactComponent as StateCancelIcon } from 'assets/svg/state-cancel.svg';
import ErrorCard from 'components/Form/Errors';

// Components
import Card from 'components/Card/Card';

const StatesModal = ({onOpen, isOpen, onClose, nextButton, isDark, changeStatusSubItem}) => {
  const textColor = useColorModeValue('#0E1633', '#8794C2');
  const cardColor = useColorModeValue('white', '#0A1027');

  const [isPanelLoading, setIsPanelLoading] = useState(false);
  const goToCustomerPortal = useCallback(async () => {
    setIsPanelLoading(true);
    const response = await apiService.goToCustomerPortal();

    if (response.message === 'Success') {
      window.location.href = response.data;
    }
    setTimeout(() => {
      setIsPanelLoading(false);
    }, 2000);
  }, []);

  const [errorCard, setErrorCard] = useState();
  const [isLoadingChangeStatusSub, setIsLoadingChangeStatusSub] = useState(false);
  const changeStatusSub = async () => {
    setIsLoadingChangeStatusSub(true);
    const data = {
      id: changeStatusSubItem.subscriptionId,
      status: 'pause'
    };

   try {
      const response = await apiService.changeStatusSubscription(data);

      if(response.statusCode !== 200){
        setErrorCard(response.message);
        setIsLoadingChangeStatusSub(false);
        return;
      }

      window.location.href = '/account/membership?success=paused';
    } catch (e){
      setErrorCard(true);
    }
    
    setIsLoadingChangeStatusSub(false);
  };

  return (
    	<> 
        <Modal isOpen={isOpen} onClose={isLoadingChangeStatusSub ? () => {} : () => {
          onClose();
          setErrorCard();
        }}>
          <ModalOverlay />
          <ModalContent className="modal__simple" 
              maxW={{ sm: '800px' }} 
              style={{ border:'1px solid #2ABDFD', overflow: 'hidden' }}>
              { !isLoadingChangeStatusSub ? <ModalCloseButton style={{zIndex: '9'}} /> : <></>}
          
              <Box bg="rgba(64, 155, 255, 0.82)" filter="blur(70px)" w="90%" h="150px"
                  transform="translate(-50%, 0px)" left="50%" 
                  top="-200px" position="absolute" borderRadius="full" />

              <Box position="relative" p="0 8px 0 8px" textAlign="center">
                  <Text 
                      variant="title-lg"
                      zIndex="3" 
                      pt="24px"
                      fontSize={{ base: '24px', md: '32px'}}
                      fontStyle="italic"
                      fontWeight="700"
                      position="relative">What would you like to do?</Text>

                  { errorCard && ( <ErrorCard mt={{base: '24px', md: '40px'}} mb={{base: '-28px'}} error={errorCard} /> )}
                  <Card padding="12px" mt={{ base: '24px', md: '40px'}} bg={cardColor}>
                    <Grid templateColumns={{ sm: '1fr', md: '1fr 0.48fr ' }} gap="30px" rowGap="24px" alignItems="center">
                      <Box>
                        <Flex gap="12px" alignItems="center">
                          <Box 
                              p="4px"
                              borderRadius="8px"
                              bg="rgba(255, 185, 27, 0.10)"
                              >
                            <StatePauseIcon fontSize="24px" />
                          </Box>
                          <Text variant="title-lg" fontSize="20px" textAlign="left">Pause Subscription</Text>
                        </Flex>
                        <Text variant="text-md" fontSize="15px" color={textColor} textAlign="left" mt="8px">
                          Once applied, your subscription will be paused for 30 days and will automatically resume afterwards. All entries remain saved.
                        </Text>
                      </Box>
                      <Button 
                        maxW={{base: '100%', md: '225px'}}
                        isLoading={isLoadingChangeStatusSub}
                        disabled={ changeStatusSubItem?.status === 'paused' ? true : false}
                        onClick={changeStatusSub}
                        variant={ isDark ? 'button-md-g' : 'button-md'}>Pause subscription</Button>
                    </Grid>
                  </Card>

                  <Card padding="12px" mt="12px" bg={cardColor}>
                    <Grid templateColumns={{ sm: '1fr', md: '1fr 0.48fr ' }} gap="30px" rowGap="24px" alignItems="center">
                      <Box>
                        <Flex gap="12px" alignItems="center">
                          <Box 
                            p="4px"
                            borderRadius="8px"
                            bg="rgba(9, 155, 255, 0.10)"
                            >
                            <StateDowngradeIcon fontSize="24px" />
                          </Box>
                          <Text variant="title-lg" fontSize="20px" textAlign="left">Downgrade Subscription</Text>
                        </Flex>
                        <Text variant="text-md" fontSize="15px" color={textColor} textAlign="left" mt="8px">
                          Downgrade your membership to a different tier so you can still join giveaways and access member-only discounts
                        </Text>
                      </Box>
                      <Button 
                        maxW={{base: '100%', md: '225px'}}
                        isLoading={isPanelLoading} 
                        onClick={goToCustomerPortal} 
                        variant={ isDark ? 'button-md-g' : 'button-md'}>Downgrade plan</Button>
                    </Grid>
                  </Card>

                  <Card padding="12px" mt="12px" bg={cardColor}>
                    <Grid templateColumns={{ sm: '1fr', md: '1fr 0.48fr ' }} gap="30px" rowGap="24px" alignItems="center">
                      <Box>
                        <Flex gap="12px" alignItems="center">
                          <Box 
                            p="4px"
                            borderRadius="8px"
                            bg="rgba(255, 185, 27, 0.10)"
                            >
                            <StateCancelIcon fontSize="24px" />
                          </Box>
                          <Text variant="title-lg" fontSize="20px" textAlign="left">Cancel My Subscription </Text>
                        </Flex>
                        <Text variant="text-md" fontSize="15px" color={textColor} textAlign="left" mt="8px">
                          Once canceled, your subscription will no longer be active and will not renew automatically.
                        </Text>
                      </Box>
                      <Button 
                        maxW={{base: '100%', md: '225px'}}
                        variant="delete-md" onClick={nextButton}>Continue cancellation</Button>
                    </Grid>
                  </Card>
              </Box>
          </ModalContent>
        </Modal> 
      </>
  );
};

export default StatesModal;